/**
 * Pure formatters for ride payloads. Reused by Dashboard cards and
 * RideDetail so the date / pace / TSS rendering stays consistent.
 */

export function formatDuration(durationSec: number | null | undefined): string {
  if (durationSec == null || durationSec <= 0) return '—';
  const totalMin = Math.round(durationSec / 60);
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  if (h === 0) return `${m}m`;
  return `${h}h ${m.toString().padStart(2, '0')}m`;
}

export function formatDistanceKm(distanceM: number | null | undefined): string {
  if (distanceM == null) return '—';
  return `${(distanceM / 1000).toFixed(1)} km`;
}

export function formatElevation(elevM: number | null | undefined): string {
  if (elevM == null) return '—';
  return `${Math.round(elevM)} m`;
}

export function formatRideDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export function formatRideDateShort(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/**
 * Color and emoji for the ride classification produced server-side
 * by services.training.metrics.classify_ride_type. Falls back to
 * a neutral chip for unclassified rides (no NP / FTP at time).
 */
export function rideTypeChip(rideType: string | null | undefined): {
  label: string;
  className: string;
} {
  const styles: Record<string, { label: string; className: string }> = {
    recovery: { label: 'Recovery', className: 'bg-blue-500/15 text-blue-400 border-blue-500/40' },
    endurance: { label: 'Endurance', className: 'bg-green-500/15 text-green-400 border-green-500/40' },
    long_endurance: { label: 'Long Endurance', className: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/40' },
    tempo: { label: 'Tempo', className: 'bg-yellow-500/15 text-yellow-400 border-yellow-500/40' },
    threshold: { label: 'Threshold', className: 'bg-orange-500/15 text-orange-400 border-orange-500/40' },
    vo2: { label: 'VO2', className: 'bg-red-500/15 text-red-400 border-red-500/40' },
    anaerobic: { label: 'Anaerobic', className: 'bg-purple-500/15 text-purple-400 border-purple-500/40' },
  };
  if (!rideType) {
    return { label: '—', className: 'bg-(--color-bg-tertiary) text-(--color-text-secondary) border-(--color-border)' };
  }
  return (
    styles[rideType] ?? {
      label: rideType,
      className: 'bg-(--color-bg-tertiary) text-(--color-text-secondary) border-(--color-border)',
    }
  );
}

/** What the TSS helpers need from a ride. */
interface RideLoad {
  avg_power: number | null;
  normalized_power: number | null;
  training_stress_score: number | null;
  tss_source?: 'power' | 'hr' | null;
}

/** Whether the ride has power data (rides from Apple Health often don't). */
export function rideHasPower(ride: RideLoad): boolean {
  return ride.avg_power != null || ride.normalized_power != null;
}

/** The TSS was estimated from heart rate (no power on the ride). */
export function tssEstimated(ride: RideLoad): boolean {
  return ride.tss_source === 'hr';
}

/** TSS for display: `—`, `62`, or `~62` when it's a heart-rate estimate. */
export function formatTss(ride: RideLoad): string {
  const tss = ride.training_stress_score;
  if (tss == null) return '—';
  const value = `${Math.round(tss)}`;
  return tssEstimated(ride) ? `~${value}` : value;
}
